/* eslint-disable react-hooks/exhaustive-deps */
import React, { forwardRef, useImperativeHandle, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
// @mui
import { Card, Typography, Button, Dialog, DialogTitle, DialogActions, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
// Canonical DRF-datetime parsing — appointment `datetime` values are the
// same "DD-MM-YYYY HH:mm:ss[.SSSSSS][Z]" wall-clock format, which bare
// moment()/new Date() mis-parse. Same helpers paymentPresentation.js uses
// for the Payments-tab table's Appointment column.
import { parseApiDate } from 'src/utils/smartReminderRules';
import { formatDate } from 'src/sections/@dashboard/payment/paymentPresentation';
// components
import { DentallyBookingFormPayment } from '../../../../components/dentally';
import { useSelector, dispatch } from '../../../../redux/store';
import { updateCompleteStatus, saveNewLeadDetail } from '../../../../redux/slices/lead';
import { getStripe, selectStripeIsLive } from '../../../../redux/slices/practiceIntegration';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Loose "has enough digits to be a phone number" check — mirrors the leniency
// used elsewhere in this codebase (server does the authoritative validation).
const PHONE_RE = /^[\d+()\-\s]{7,}$/;

// Map saved Redux payment state back to the DentallyBookingFormPayment values format
const mapFromRedux = (payment, type, leadContact) => {
    const defaultRecipients = {
        recipientEmail: payment?.recipientEmail ?? leadContact?.email ?? '',
        recipientPhone: payment?.recipientPhone ?? leadContact?.phone ?? '',
    };
    if (!payment || payment.payment_type === 'No') {
        return {
            paymentOption: type === 'lead' ? 'received' : 'none',
            paymentType: 'DEPOSIT',
            paymentAmount: payment?.amount || '',
            receivedMode: 'PHONE',
            paymentLinkChannel: 'email',
            ...defaultRecipients,
        };
    }
    const isLink = payment.mode_of_payment === 'SEND';
    return {
        paymentOption: isLink ? 'link' : 'received',
        paymentType: payment.payment_type || 'DEPOSIT',
        paymentAmount: payment.amount || '',
        receivedMode: isLink ? 'PHONE' : (payment.mode_of_payment || 'PHONE'),
        paymentLinkChannel: payment.paymentLinkChannel || 'email',
        ...defaultRecipients,
    };
};

// Whether the current channel selection + recipient values are enough to submit.
const recipientsAreValid = (channel, email, phone) => {
    const needsEmail = channel === 'email' || channel === 'both';
    const needsPhone = channel === 'whatsapp' || channel === 'both';
    if (needsEmail && !EMAIL_RE.test((email || '').trim())) return false;
    if (needsPhone && !PHONE_RE.test((phone || '').trim())) return false;
    return true;
};

const PaymentOption = forwardRef(({ handleNext, type, onValidityChange }, ref) => {
    const { newLeadDetail: { payment }, leadDetail, followLead } = useSelector((state) => state.lead);
    const { newLeadDetail } = useSelector((state) => state.lead);
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    // Was `stripeConnected.length === 0`, which throws outright whenever the
    // store holds the boolean form of this value, and reads a disconnected
    // account as usable.
    const stripeIsLive = useSelector(selectStripeIsLive);
    // TODO(active-treatment-removal): pass the treatment explicitly into PaymentOption
    // instead of reading the currentTreatmentDataGlobal action-context bridge.
    const currentTreatmentDataGlobal = useSelector((state) => state.multiTreatment?.currentTreatmentDataGlobal);

    const [open, toggleOpen] = useState(false);
    const [values, setValues] = useState(() => mapFromRedux(payment, type, leadDetail?.lead_contact));
    const [recipientTouched, setRecipientTouched] = useState(false);

    // "For appointment" select (contract: Payment create accepts an optional
    // `appointment` id) — lists this treatment's upcoming/recent
    // appointments, defaulting to the nearest upcoming BOOKED one so the
    // common case (taking a deposit for the appointment just booked) needs
    // no extra click. `followLead.lead_appointment` is the same
    // per-lead appointment array OverView.jsx's Upcoming/Attended tables
    // read from; leadDetail's own list is a fallback for callers where
    // followLead isn't populated (e.g. AI booking / non-FollowUpV2 paths).
    const activeTreatmentId = currentTreatmentDataGlobal?.treatmentData?.id || newLeadDetail?.lead_treatment?.id || leadDetail?.lead_treatments?.[0]?.id;
    // Invalid/unparseable dates sort as 0 (oldest) rather than NaN, which
    // would otherwise corrupt the sort ordering (matches
    // useTreatmentPaymentStatus.js's toSortValue convention).
    const toSortValue = (value) => parseApiDate(value)?.valueOf() || 0;

    const treatmentAppointments = useMemo(() => {
        const all = followLead?.lead_appointment || leadDetail?.lead_appointment || [];
        return (Array.isArray(all) ? all : [])
            .filter((a) => activeTreatmentId == null || String(a?.lead_treatment) === String(activeTreatmentId))
            .filter((a) => a?.status !== 'CANCELLED')
            .sort((a, b) => toSortValue(b?.datetime) - toSortValue(a?.datetime));
    }, [followLead, leadDetail, activeTreatmentId]);

    const defaultAppointmentId = useMemo(() => {
        // A caller that already knows exactly which appointment this payment
        // is for (e.g. BookedPaymentStatusCard's "Take deposit"/"Take another
        // payment" on the Overview tab) wins over the generic default.
        if (currentTreatmentDataGlobal?.appointmentId != null) return currentTreatmentDataGlobal.appointmentId;
        const now = Date.now();
        const upcomingBooked = treatmentAppointments
            .filter((a) => a?.status === 'BOOKED' && toSortValue(a?.datetime) > now)
            .sort((a, b) => toSortValue(a?.datetime) - toSortValue(b?.datetime));
        return upcomingBooked[0]?.id ?? '';
    }, [treatmentAppointments, currentTreatmentDataGlobal]);

    const [appointmentId, setAppointmentId] = useState('');
    useEffect(() => {
        setAppointmentId(defaultAppointmentId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultAppointmentId]);

    useEffect(() => {
        if (practiceId) {
            dispatch(getStripe(practiceId));
        }
    }, [practiceId]);

    // #91 — emit validity for parents (FollowUpV2) that gate a "Next" button on
    // payment selection. V1's OpenContactLeadPopup stepper ignores this prop, so
    // V1 behavior is unchanged. Rules mirror onSubmit's silent-return at L59-62.
    useEffect(() => {
        if (typeof onValidityChange !== 'function') return;
        if (values.paymentOption === 'none') {
            onValidityChange(true);
            return;
        }
        const amt = parseFloat(values.paymentAmount);
        const amountValid = Number.isFinite(amt) && amt > 0;
        if (values.paymentOption === 'link') {
            onValidityChange(amountValid && recipientsAreValid(values.paymentLinkChannel, values.recipientEmail, values.recipientPhone));
            return;
        }
        onValidityChange(amountValid);
    }, [values.paymentOption, values.paymentAmount, values.paymentLinkChannel, values.recipientEmail, values.recipientPhone, onValidityChange]);

    const handleChange = (field, val) => {
        setValues(prev => ({ ...prev, [field]: val }));

        // Check Stripe connection when switching to payment link
        if (field === 'paymentOption' && val === 'link' && !stripeIsLive) {
            toggleOpen(true);
        }
        if (field === 'recipientEmail' || field === 'recipientPhone') {
            setRecipientTouched(true);
        }
    };

    const onSubmit = (status) => {
        // Validate amount when payment is selected
        if (values.paymentOption !== 'none') {
            const amt = parseFloat(values.paymentAmount);
            if (!amt || amt <= 0) return;
        }
        // Recipient email/phone must be present and valid before a link can be sent.
        if (values.paymentOption === 'link' && !recipientsAreValid(values.paymentLinkChannel, values.recipientEmail, values.recipientPhone)) {
            setRecipientTouched(true);
            return;
        }

        // Map to the existing Redux save format
        const paymentData = {};
        if (values.paymentOption === 'none') {
            paymentData.payment_type = 'No';
            paymentData.mode_of_payment = '';
            paymentData.amount = 0;
        } else if (values.paymentOption === 'link') {
            paymentData.payment_type = values.paymentType;
            paymentData.mode_of_payment = 'SEND';
            paymentData.amount = parseFloat(values.paymentAmount) || 0;
            paymentData.paymentLinkChannel = values.paymentLinkChannel;
            paymentData.recipientEmail = (values.recipientEmail || '').trim();
            paymentData.recipientPhone = (values.recipientPhone || '').trim();
        } else {
            paymentData.payment_type = values.paymentType;
            paymentData.mode_of_payment = values.receivedMode;
            paymentData.amount = parseFloat(values.paymentAmount) || 0;
        }
        if (values.paymentOption !== 'none' && appointmentId) {
            paymentData.appointment = appointmentId;
        }

        dispatch(saveNewLeadDetail({ payment: paymentData }));
        if (status?.toLowerCase?.() === 'finish') {
            dispatch(updateCompleteStatus(true));
        }
        // handleNext is the caller's explicit "process this" callback — guard it
        // since some callers (e.g. OpenPayment's Finish button) only wire this up
        // for the terminal submit and would otherwise throw (Sentry JAVASCRIPT-REACT-D9).
        if (typeof handleNext === 'function') {
            handleNext(true);
        }
    };

    useImperativeHandle(
        ref, () => ({
            async submit(status) {
                onSubmit(status);
            },
        }),
        [values, appointmentId]
    );

    const location = window.location.origin;
    const handleClose = () => toggleOpen(false);
    const goToPracticeIntegrationPage = () => {
        window.open(`${location}/dashboard/user/integration`, '_blank');
        toggleOpen(false);
    };

    return (
        <>
            <Card sx={{ p: 3, boxShadow: 'none' }}>
                <Typography variant='subtitle2' sx={{ mb: 1, fontSize: 13, fontWeight: 600 }}>
                    Take a deposit or consultation fee for {newLeadDetail?.lead_treatment?.treatment || currentTreatmentDataGlobal?.treatmentData?.treatment || leadDetail?.lead_treatments?.[0]?.treatment || ''}?
                </Typography>
                <DentallyBookingFormPayment
                    values={values}
                    onChange={handleChange}
                    errors={recipientTouched ? {
                        recipientEmail: (values.paymentLinkChannel === 'email' || values.paymentLinkChannel === 'both') && !EMAIL_RE.test((values.recipientEmail || '').trim())
                            ? 'Enter a valid email address' : '',
                        recipientPhone: (values.paymentLinkChannel === 'whatsapp' || values.paymentLinkChannel === 'both') && !PHONE_RE.test((values.recipientPhone || '').trim())
                            ? 'Enter a valid phone number' : '',
                    } : {}}
                    showRecipientFields
                />
                {values.paymentOption !== 'none' && treatmentAppointments.length > 0 && (
                    <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                        <InputLabel id="payment-for-appointment-label">For appointment (optional)</InputLabel>
                        <Select
                            labelId="payment-for-appointment-label"
                            label="For appointment (optional)"
                            value={appointmentId}
                            onChange={(e) => setAppointmentId(e.target.value)}
                            data-testid="payment-for-appointment-select"
                        >
                            <MenuItem value="">None</MenuItem>
                            {treatmentAppointments.map((a) => (
                                <MenuItem key={a.id} value={a.id}>
                                    {formatDate(a?.datetime, 'DD MMM YYYY, hh:mm A')} · {a?.status ? a.status.replace(/_/g, ' ') : ''}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            </Card>
            <Dialog open={open} onClose={handleClose}>
                <DialogTitle sx={{ padding: '40px 40px 0px', textAlign: 'center' }}>Stripe connection is required to send an online payment link. Do you want to redirect to the Practice Integration page to connect your Stripe account?</DialogTitle>
                <DialogActions sx={{ padding: '40px !important', justifyContent: 'center' }}>
                    <Button sx={{ margin: '0px 10px', padding: '5px 20px', fontSize: '14px', boxShadow: 'none', border: '1px solid rgba(145, 158, 171, 0.32)', borderRadius: '8px', minWidth: '100px' }} onClick={handleClose}>No</Button>
                    <Button type="submit" sx={{ margin: '0px 10px', padding: '5px 20px', fontSize: '14px', boxShadow: '0px 8px 16px rgba(0, 171, 85, 0.24)', borderRadius: '8px', bgcolor: '#00AB55 !important', color: '#fff', minWidth: '100px' }} onClick={goToPracticeIntegrationPage}>Yes</Button>
                </DialogActions>
            </Dialog>
        </>
    );
});

PaymentOption.propTypes = {
    handleNext: PropTypes.func,
    type: PropTypes.string,
    onValidityChange: PropTypes.func,
};
export default PaymentOption;
